/**
 * catalogueImport: one parser and one validator for every Lusuku vertical.
 * ───────────────────────────────────────────────────────────────────────────
 * Pure: no React, no network, no DOM beyond the File objects the operator
 * picked. Everything here is decided offline so the review table can tell the
 * truth BEFORE a single row is written, and so the rules can be tested without
 * mounting a component.
 *
 * WHY ONE MODULE AND NOT ONE PER VERTICAL. Brand and healthcare agree on every
 * rule that matters and disagree only about which columns exist. They agree
 * that an out-of-stock row is skipped rather than imported as one-in-stock,
 * that a cover image is mandatory, that a re-run must not duplicate, that a
 * price of 0 is an error unless the seller deliberately sells by quote, and
 * that an unknown availability word is refused rather than guessed. Every one
 * of those is a rule a second copy would eventually get wrong on one vertical
 * only, which is the worst way to be wrong: nobody notices until a pharmacy
 * has sold something for nothing.
 *
 * So the shape is a factory. `createCatalogueImport(dialect)` returns the
 * parser and validator bound to one vertical's columns, and the dialect
 * supplies only what genuinely differs:
 *
 *   columns       the vertical's own header resolver and labels
 *   vendorNoun    the word used in operator messages ("brand", "pharmacy")
 *   supportsSpecs whether `spec:` columns mean anything on this catalogue
 *   readExtras    extra fields to parse off a row
 *   validateExtras extra checks, returning the resolved extra fields
 *
 * NEVER SENT TO THE API by any vertical: commissionRate, commissionAmount,
 * vendorNetPrice, commissionCalculatedAt, commissionRateSource and country. All
 * are derived server-side. `price_ugx` is the GROSS customer-facing price; the
 * server derives the vendor's net from it. A column for any of them would be a
 * column that does nothing, or worse, one that looks like it does something.
 *
 * HEADERS ARE READ BY MEANING. "Price (UGX)" and "price_ugx" are the same
 * column: the vertical's columns file holds the one list of labels and aliases,
 * and everything below works in the snake_case field keys it resolves them to.
 */
import * as XLSX from "xlsx";
import { SPEC_PREFIX, STATUS } from "./catalogueImportColumns.js";

/* config/productPaymentPolicy.js — mirrored, not imported, because this file
   must stay free of backend imports. Keep in step with the enum on every
   product schema's requiredPaymentMethod. */
export const PAYMENT_METHODS = Object.freeze([
  "NONE",
  "ANY_ONLINE",
  "MOMO",
  "AIRTEL",
  "VISA",
  "ZAMTEL",
]);

/* Cap enforced server-side by each vertical's sanitiser for additionalImages.
   Extras beyond this are silently dropped there, so the importer warns. */
export const MAX_ADDITIONAL_IMAGES = 5;

/* Grey example rows in the shipped templates carry this in `notes`; the parser
   drops them so a template that was never cleaned out cannot import itself. */
const EXAMPLE_ROW_MARKER = "delete this example row";

/* Availability vocabulary. Deliberately generous on input — operators export
   from Jumia, from SAP, from a WhatsApp message — and deliberately CLOSED:
   an unrecognised value is an error, never a guess. Guessing "In transit"
   means in stock is how an out-of-stock product goes on sale. */
const IN_STOCK = new Set([
  "in stock",
  "instock",
  "in-stock",
  "available",
  "yes",
  "y",
  "true",
  "1",
]);
const OUT_OF_STOCK = new Set([
  "out of stock",
  "outofstock",
  "out-of-stock",
  "unavailable",
  "no",
  "n",
  "false",
  "0",
  "sold out",
  "soldout",
  "backorder",
  "back order",
  "pre-order",
  "preorder",
  "discontinued",
  "coming soon",
]);

const SHEET_KINDS = {
  sections: /sections\s*$/i,
  products: /products\s*$/i,
};

const REQUIRED_HEADERS = {
  sections: ["section_title"],
  products: ["name", "section_title"],
};

// ── Small parsers ──────────────────────────────────────────────────────────

export const norm = (v) => String(v ?? "").trim().toLowerCase();
export const str = (v) => String(v ?? "").trim();

/** "UGX 1,250,000" → 1250000. Returns null when there is nothing to parse. */
export const parseMoney = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const cleaned = String(v).replace(/[^0-9.-]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === ".") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

/** Unrecognised input falls back rather than throwing — blank means default. */
export const parseBool = (v, fallback = true) => {
  if (v === null || v === undefined || v === "") return fallback;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  const s = norm(v);
  if (["true", "yes", "y", "1"].includes(s)) return true;
  if (["false", "no", "n", "0"].includes(s)) return false;
  return fallback;
};

/** Comma / semicolon / newline separated cell → trimmed, de-blanked array. */
export const parseList = (v) => {
  if (v === null || v === undefined || v === "") return [];
  return String(v)
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
};

/* Status vocabulary for the enabled column. The blueprint writes Active or
   Deactivated; older sheets wrote yes / no, which still work. */
const ACTIVE_WORDS = new Set([norm(STATUS.ACTIVE), "live", "online", "enabled"]);
const INACTIVE_WORDS = new Set([
  norm(STATUS.DEACTIVATED),
  "inactive",
  "disabled",
  "offline",
]);

/** Status cell → boolean. Active / Deactivated first, then yes / no. */
export const parseStatus = (v, fallback = true) => {
  const s = norm(v);
  if (ACTIVE_WORDS.has(s)) return true;
  if (INACTIVE_WORDS.has(s)) return false;
  return parseBool(v, fallback);
};

/** "Branded packaging" → "BRANDED_PACKAGING", so an enum reads either way. */
export const enumValue = (v) => str(v).toUpperCase().replace(/[\s-]+/g, "_");

/**
 * Availability → one of "in" | "out" | "unknown" | "blank".
 * Exported because the review table and the tests both need the same verdict.
 */
export const readAvailability = (v) => {
  const s = norm(v);
  if (!s) return "blank";
  if (IN_STOCK.has(s)) return "in";
  if (OUT_OF_STOCK.has(s)) return "out";
  return "unknown";
};

const fileKey = (name) => norm(name);

// ── Sheet machinery ────────────────────────────────────────────────────────

export const makeFindHeaderRow = (canonicalHeader) => (rows, kind) => {
  const required = REQUIRED_HEADERS[kind] || [];
  const limit = Math.min(rows.length, 8);
  for (let i = 0; i < limit; i += 1) {
    const cells = (rows[i] || []).map((c) => canonicalHeader(kind, c));
    if (required.every((h) => cells.includes(h))) {
      return { headerRow: i, headers: cells };
    }
  }
  return null;
};

const rowsToObjects = (rows, headerRow, headers) => {
  const out = [];
  for (let i = headerRow + 1; i < rows.length; i += 1) {
    const row = rows[i] || [];
    if (
      row.every((c) => c === null || c === undefined || String(c).trim() === "")
    ) {
      continue;
    }
    const obj = {};
    headers.forEach((h, idx) => {
      if (h) obj[h] = row[idx];
    });
    obj.__row = i + 1; // 1-based, so it matches what the operator sees in Excel
    out.push(obj);
  }
  return out;
};

const isExampleRow = (obj) => norm(obj.notes).includes(EXAMPLE_ROW_MARKER);

/**
 * Pick a sheet BY ROW COUNT among those whose name matches, not by name alone.
 *
 * The trap this exists for: an operator starts from a workbook that still
 * contains a filled example sheet, so two sheets match "Products" and the one
 * with the example data can outvote the one with their own rows. Whichever has
 * more surviving data rows wins, and the chosen name is surfaced in the review
 * so the operator can see which sheet was actually read.
 */
export const makePickSheet = (findHeaderRow) => (workbook, kind) => {
  const re = SHEET_KINDS[kind];
  let best = null;
  for (const sheetName of workbook.SheetNames) {
    if (!re.test(sheetName)) continue;
    const ws = workbook.Sheets[sheetName];
    if (!ws) continue;
    const rows = XLSX.utils.sheet_to_json(ws, {
      header: 1,
      blankrows: false,
      defval: null,
    });
    const found = findHeaderRow(rows, kind);
    if (!found) continue;
    const objects = rowsToObjects(rows, found.headerRow, found.headers).filter(
      (o) => !isExampleRow(o)
    );
    if (!best || objects.length > best.rows.length) {
      best = { sheetName, rows: objects, headers: found.headers };
    }
  }
  return best;
};

// ── Readers ────────────────────────────────────────────────────────────────

const readSections = (rows) => {
  const seen = new Map();
  for (const r of rows) {
    const title = str(r.section_title);
    if (!title) continue;
    const key = norm(title);
    if (seen.has(key)) continue; // first definition wins
    seen.set(key, {
      title,
      description: str(r.section_description),
      order: parseMoney(r.section_order),
      sourceRow: r.__row,
    });
  }
  return [...seen.values()];
};

/** Column headers that become `specs` keys, in sheet order. */
export const readSpecColumns = (headers = []) =>
  headers
    .filter((h) => typeof h === "string" && h.startsWith(SPEC_PREFIX))
    .map((h) => ({ header: h, fieldKey: h.slice(SPEC_PREFIX.length).trim() }))
    .filter((c) => c.fieldKey);

/** The cell parsers a dialect's readExtras and validateExtras are handed. */
const PARSERS = Object.freeze({
  parseMoney,
  parseBool,
  parseList,
  parseStatus,
  enumValue,
  norm,
  str,
});

/**
 * Where a product's cover (or extra angle) comes from, in strict precedence.
 *
 * A Drive / Jumia / supplier page link is NEVER an image source. It 403s the
 * moment the file is private or is a shortcut to someone else's file, and a
 * catalogue whose photos hotlink a competitor's CDN is not a catalogue. Only a
 * real cloudinary_url or a file the operator actually picked counts.
 */
const resolveImage = (filename, cloudinaryUrl, byName) => {
  if (cloudinaryUrl && /^https?:\/\//i.test(cloudinaryUrl)) {
    return { kind: "url", url: cloudinaryUrl, filename: filename || "" };
  }
  if (filename) {
    const file = byName.get(fileKey(filename));
    if (file) return { kind: "file", file, filename };
    return { kind: "missing", filename };
  }
  return { kind: "none", filename: "" };
};

const flattenExistingProducts = (existingSections = []) => {
  const bySku = new Map();
  const bySectionName = new Map();
  for (const section of existingSections || []) {
    for (const p of section?.products || []) {
      if (!p || !p._id) continue;
      const label = {
        name: p.name,
        skuCode: p.skuCode,
        sectionTitle: section.title,
      };
      if (p.skuCode) bySku.set(norm(p.skuCode), label);
      // Names may repeat inside a section, so every product keeps its place.
      const nameKey = `${norm(section.title)}::${norm(p.name)}`;
      if (!bySectionName.has(nameKey)) bySectionName.set(nameKey, []);
      bySectionName.get(nameKey).push(label);
    }
  }
  return { bySku, bySectionName };
};

// ── The factory ────────────────────────────────────────────────────────────

/**
 * Bind the parser and validator to one vertical's columns.
 *
 * @param {object}   dialect
 * @param {Function} dialect.canonicalHeader  (kind, rawHeader) => fieldKey
 * @param {Function} dialect.labelOf          (kind, fieldKey) => Title Case label
 * @param {string}   dialect.vendorNoun       the word operator messages use
 * @param {boolean}  [dialect.supportsSpecs]  whether `spec:` columns are read
 * @param {Function} [dialect.readExtras]     (row, parsers) => extra parsed fields
 * @param {Function} [dialect.validateExtras] ({item, ctx, errors, warnings, parsers}) => extra resolved fields
 */
export const createCatalogueImport = (dialect) => {
  const {
    canonicalHeader,
    labelOf,
    vendorNoun,
    supportsSpecs = false,
    readExtras = () => ({}),
    validateExtras = () => ({}),
  } = dialect;

  const findHeaderRow = makeFindHeaderRow(canonicalHeader);
  const pickSheet = makePickSheet(findHeaderRow);

  /** Product column label, for messages an operator reads. */
  const P = (key) => labelOf("products", key);

  const readProducts = (rows, specColumns) =>
    rows.map((r) => {
      const specs = {};
      for (const col of specColumns) {
        const raw = r[col.header];
        if (raw === null || raw === undefined || String(raw).trim() === "")
          continue;
        specs[col.fieldKey] = typeof raw === "number" ? raw : str(raw);
      }
      return {
        sourceRow: r.__row,
        name: str(r.name),
        sectionTitle: str(r.section_title),
        description: str(r.description),
        price: parseMoney(r.price_ugx),
        priceOnRequest: parseBool(r.price_on_request, false),
        skuCode: str(r.sku_code),
        availabilityRaw: str(r.availability),
        availability: readAvailability(r.availability),
        imageFilename: str(r.image_filename),
        cloudinaryUrl: str(r.cloudinary_url),
        imageSource: str(r.image_source),
        additionalFilenames: parseList(r.additional_image_filenames),
        countInStock: parseMoney(r.count_in_stock),
        enabled: parseStatus(r.enabled, true),
        tags: parseList(r.tags),
        categoryNames: parseList(r.platform_categories),
        unitName: str(r.unit),
        measurements: parseMoney(r.measurements),
        minOrderQty: parseMoney(r.min_order_qty),
        requiredPaymentMethod: enumValue(r.required_payment_method),
        specs,
        notes: str(r.notes),
        ...readExtras(r, PARSERS),
      };
    });

  /**
   * @param {object} parsed        output of parseWorkbook
   * @param {File[]} imageFiles    photos the operator picked
   * @param {object} ctx
   * @param {Array}  ctx.existingSections   the vendor's current sections
   * @param {Array}  ctx.platformCategories [{_id, category}] from /category/get-all
   * @param {Array}  ctx.units              [{_id, unit}] from /unit
   */
  const validateImport = (parsed, imageFiles = [], ctx = {}) => {
    const {
      existingSections = [],
      platformCategories = [],
      units = [],
    } = ctx;

    const byName = new Map();
    for (const f of imageFiles) byName.set(fileKey(f.name), f);
    const usedFiles = new Set();

    const categoryByName = new Map(
      (platformCategories || [])
        .filter((c) => c && c._id && c.category)
        .map((c) => [norm(c.category), c])
    );
    /* Old names and other names resolve too ("Stationary" -> Stationery),
       but never over a category's own name. */
    (platformCategories || []).forEach((c) => {
      if (!c || !c._id || !c.category) return;
      (c.aliases || []).forEach((alias) => {
        const key = norm(alias);
        if (key && !categoryByName.has(key)) categoryByName.set(key, c);
      });
    });
    const unitByName = new Map(
      (units || [])
        .filter((u) => u && u._id && u.unit)
        .map((u) => [norm(u.unit), u])
    );
    /* A unit's other spellings ("kilograms", "pieces") resolve to it, but a
       spelling that is itself a unit's name keeps meaning that unit, so a
       sheet that says "Kgs" files exactly where it always did. */
    (units || []).forEach((u) => {
      if (!u || !u._id || !u.unit) return;
      (u.aliases || []).forEach((alias) => {
        const key = norm(alias);
        if (key && !unitByName.has(key)) unitByName.set(key, u);
      });
    });

    const { bySku, bySectionName } = flattenExistingProducts(existingSections);

    // ── Sections: declared, plus any a product row references ──────────────
    const sections = [...(parsed.sections || [])];
    const declared = new Set(sections.map((s) => norm(s.title)));
    for (const item of parsed.products || []) {
      const key = norm(item.sectionTitle);
      if (key && !declared.has(key)) {
        declared.add(key);
        sections.push({
          title: item.sectionTitle,
          description: "",
          order: null,
          sourceRow: item.sourceRow,
          inferred: true,
        });
      }
    }
    const existingByTitle = new Map(
      (existingSections || [])
        .filter((s) => s && s._id && s.title)
        .map((s) => [norm(s.title), s])
    );
    const resolvedSections = sections.map((s) => {
      const match = existingByTitle.get(norm(s.title));
      return {
        ...s,
        action: match ? "existing" : "create",
        existingId: match ? match._id : null,
      };
    });

    // ── Products ───────────────────────────────────────────────────────────
    const seenInFile = new Map();
    const items = (parsed.products || []).map((it) => {
      const errors = [];
      const warnings = [];
      let skipReason = null;

      // Availability first — a skipped row is not worth validating further, and
      // burying "out of stock" under six unrelated errors helps nobody.
      if (it.availability === "out") {
        skipReason = `not in stock (availability: "${it.availabilityRaw}")`;
      } else if (it.availability === "unknown") {
        errors.push(
          `availability "${it.availabilityRaw}" is not recognised — use "In Stock" or "Out of Stock"`
        );
      }

      if (!it.name) errors.push(`${P("name")} is required`);
      if (!it.sectionTitle) errors.push(`${P("section_title")} is required`);

      /* Price. Zero is legitimate ONLY for a price-on-request product, which is
         the platform floor rather than a brand rule: founder rule of 29 Sep
         2026, no item may sell at 0. A quoted item carries price 0 WITH the
         posture set; a bare 0 is refused here so it never reaches a catalogue
         as a USh 0 product with an Add to Cart button. */
      if (it.priceOnRequest) {
        if (it.price !== null && it.price > 0) {
          warnings.push(
            `${P("price_on_request")} is Yes, so ${P("price_ugx")} will be sent as 0`
          );
        }
      } else if (it.price === null) {
        errors.push(
          `${P("price_ugx")} is required (or set ${P("price_on_request")} to Yes)`
        );
      } else if (it.price <= 0) {
        errors.push(
          `${P("price_ugx")} must be greater than 0 unless ${P("price_on_request")} is Yes`
        );
      } else if (!Number.isInteger(it.price)) {
        warnings.push(`price ${it.price} is not a whole number and will be rounded`);
      }

      /* A cover that is knowingly a stand-in. Vendors routinely have a price
         list weeks before they have photography, and the choice is between a
         catalogue that cannot be built and one built on a marked placeholder.
         The mark is what makes the second option honest: `imageSource` is a
         real field on the product, so these stay findable and filterable
         instead of becoming 71 products nobody remembers to re-shoot. */
      const isPlaceholder = it.imageSource
        .toUpperCase()
        .startsWith("PLACEHOLDER");
      if (isPlaceholder) {
        warnings.push(
          "cover is a placeholder — replace it with the real photograph before this product goes live"
        );
      }

      // Cover image. Required by every product schema; the server 400s without it.
      const cover = resolveImage(it.imageFilename, it.cloudinaryUrl, byName);
      if (cover.kind === "file") usedFiles.add(fileKey(cover.filename));
      if (cover.kind === "missing") {
        errors.push(
          `no picked file named "${it.imageFilename}" (a cover image is required on every product)`
        );
      } else if (cover.kind === "none") {
        errors.push(`${P("image_filename")} or ${P("cloudinary_url")} is required`);
      }

      // Extra angles.
      let additional = it.additionalFilenames.map((fn) => {
        const r = resolveImage(fn, "", byName);
        if (r.kind === "file") usedFiles.add(fileKey(fn));
        return r;
      });
      if (additional.length > MAX_ADDITIONAL_IMAGES) {
        warnings.push(
          `${additional.length} extra angles listed; only the first ${MAX_ADDITIONAL_IMAGES} will be used`
        );
        additional = additional.slice(0, MAX_ADDITIONAL_IMAGES);
      }
      for (const a of additional) {
        if (a.kind === "missing") {
          warnings.push(
            `extra angle "${a.filename}" was not picked and will be skipped`
          );
        }
      }
      additional = additional.filter((a) => a.kind === "file" || a.kind === "url");

      // The one enum every catalogue carries.
      const payment = it.requiredPaymentMethod || "NONE";
      if (!PAYMENT_METHODS.includes(payment)) {
        errors.push(
          `${P("required_payment_method")} "${it.requiredPaymentMethod}" is not one of ${PAYMENT_METHODS.join(" / ")}`
        );
      }

      // Numbers that must not be negative.
      if (it.countInStock !== null && it.countInStock < 0) {
        errors.push(`${P("count_in_stock")} cannot be negative`);
      }
      if (it.countInStock === 0) {
        // Every create path coerces `countInStock || 1`, so a 0 arrives as 1.
        warnings.push(
          `${P("count_in_stock")} is 0; the server stores a minimum of 1, so set ${P("availability")} to Out of Stock instead`
        );
      }
      if (it.minOrderQty !== null && it.minOrderQty < 1) {
        errors.push(`${P("min_order_qty")} must be at least 1`);
      }

      // Platform categories → ids. Unknown names warn; the product still imports
      // uncategorised rather than failing over a merchandising field.
      const categoryIds = [];
      for (const nameRaw of it.categoryNames) {
        const match = categoryByName.get(norm(nameRaw));
        if (match) categoryIds.push(match._id);
        else
          warnings.push(
            `platform category "${nameRaw}" does not exist on Lusuku and will be skipped`
          );
      }
      if (!categoryIds.length && !skipReason) {
        // Uncategorised products sort last in /shop and appear under no chip on
        // the category rail, which for a flagship vendor is a real loss.
        warnings.push(
          `no ${P("platform_categories")} matched, so this product will not appear under any category filter`
        );
      }

      // Unit.
      let unitId;
      if (it.unitName) {
        const match = unitByName.get(norm(it.unitName));
        if (match) unitId = match._id;
        else
          warnings.push(
            `unit "${it.unitName}" is not on Lusuku's unit list and will be skipped`
          );
      }

      // Whatever this vertical has that the others do not.
      const extras = validateExtras({ item: it, ctx, errors, warnings, parsers: PARSERS, P });

      /* Duplicates — inside the file, and against what the vendor already has.
         Names may repeat inside a section (a shop sells four "Cleaning
         Brush"es); a Barcode is what tells them apart. So a same-named row is a
         duplicate only when nothing distinguishes it: one side has no Barcode,
         or both carry the same one. */
      let duplicateOf = null;
      const nameKey = `${norm(it.sectionTitle)}::${norm(it.name)}`;
      const skuKey = norm(it.skuCode);
      if (it.name && it.sectionTitle) {
        const prior = seenInFile.get(nameKey) || [];
        const clash = prior.find((p) => !skuKey || !p.sku || p.sku === skuKey);
        if (clash) {
          const why =
            skuKey && clash.sku === skuKey
              ? `same ${P("name")} and ${P("sku_code")}`
              : `same ${P("name")}, and no ${P("sku_code")} to tell them apart`;
          errors.push(`duplicate of row ${clash.row} in the same section (${why})`);
        }
        prior.push({ row: it.sourceRow, sku: skuKey });
        seenInFile.set(nameKey, prior);
      }
      if (skuKey && bySku.has(skuKey)) {
        duplicateOf = bySku.get(skuKey);
      } else {
        const sameName = bySectionName.get(nameKey) || [];
        duplicateOf = sameName.find((p) => !skuKey || !p.skuCode) || null;
      }
      if (duplicateOf && !skipReason) {
        skipReason = `already on this ${vendorNoun}${
          duplicateOf.skuCode ? ` as ${duplicateOf.skuCode}` : ""
        } — re-running an import never duplicates`;
      }

      return {
        ...it,
        cover,
        additional,
        isPlaceholder,
        categoryIds,
        unitId,
        requiredPaymentMethod: payment,
        ...extras,
        duplicateOf,
        skipReason,
        errors: skipReason ? [] : errors,
        warnings: skipReason ? [] : warnings,
        skipped: !!skipReason,
        ok: !skipReason && errors.length === 0,
      };
    });

    // Only sections that a row we will actually create needs.
    const neededSections = new Set(
      items.filter((i) => i.ok).map((i) => norm(i.sectionTitle))
    );
    const sectionsForPlan = resolvedSections.filter((s) =>
      neededSections.has(norm(s.title))
    );

    const unusedFiles = [...byName.keys()].filter((k) => !usedFiles.has(k));

    const totals = {
      sectionsToCreate: sectionsForPlan.filter((s) => s.action === "create").length,
      sectionsExisting: sectionsForPlan.filter((s) => s.action === "existing").length,
      itemsOk: items.filter((i) => i.ok).length,
      itemsFailed: items.filter((i) => !i.ok && !i.skipped).length,
      itemsWithWarnings: items.filter((i) => i.ok && i.warnings.length).length,
      itemsOutOfStock: items.filter((i) => i.availability === "out").length,
      itemsAlreadyImported: items.filter((i) => i.skipped && i.duplicateOf).length,
      itemsPlaceholderCover: items.filter((i) => i.ok && i.isPlaceholder).length,
      imagesToUpload: items
        .filter((i) => i.ok)
        .reduce(
          (n, i) =>
            n +
            (i.cover.kind === "file" ? 1 : 0) +
            i.additional.filter((a) => a.kind === "file").length,
          0
        ),
    };

    return {
      sheetsUsed: parsed.sheetsUsed,
      specColumns: parsed.specColumns,
      hasAvailabilityColumn: parsed.hasAvailabilityColumn,
      sections: sectionsForPlan,
      allSections: resolvedSections,
      items,
      unusedFiles,
      totals,
      canCommit: items.some((i) => i.ok),
    };
  };

  const parseWorkbook = (arrayBuffer) => {
    const NOT_A_SPREADSHEET =
      "That file could not be read as a spreadsheet. Export it as .xlsx and try again.";

    /* XLSX.read almost never throws: handed a PDF, a Word file or an image it
       falls back to its text parser and returns a workbook containing one sheet
       called "Sheet1" full of nonsense. The operator would then be told "no
       products sheet found" and go hunting for a tab in a file that was never a
       spreadsheet. Two magic numbers settle it before we read a thing:
         PK\x03\x04           — a zip, i.e. .xlsx / .xlsm
         \xD0\xCF\x11\xE0     — an OLE2 compound file, i.e. legacy .xls
       A .csv is rejected too, and correctly: it cannot carry the Sections sheet,
       and "export it as .xlsx" is the right instruction. */
    const bytes =
      arrayBuffer instanceof Uint8Array
        ? arrayBuffer
        : new Uint8Array(arrayBuffer || []);
    const startsWith = (sig) => sig.every((b, i) => bytes[i] === b);
    const looksLikeSpreadsheet =
      bytes.length >= 4 &&
      (startsWith([0x50, 0x4b, 0x03, 0x04]) ||
        startsWith([0xd0, 0xcf, 0x11, 0xe0]));
    if (!looksLikeSpreadsheet) throw new Error(NOT_A_SPREADSHEET);

    let workbook;
    try {
      workbook = XLSX.read(bytes, { type: "array" });
    } catch (err) {
      throw new Error(NOT_A_SPREADSHEET);
    }
    if (!workbook || !workbook.SheetNames || !workbook.SheetNames.length) {
      throw new Error(NOT_A_SPREADSHEET);
    }

    const productSheet = pickSheet(workbook, "products");
    if (!productSheet) {
      throw new Error(
        `No products sheet found. The workbook needs a sheet whose name ends in "Products" with ${P("name")} and ${P("section_title")} columns.`
      );
    }
    const sectionSheet = pickSheet(workbook, "sections");
    const specColumns = supportsSpecs ? readSpecColumns(productSheet.headers) : [];

    return {
      sheetsUsed: {
        products: productSheet.sheetName,
        sections: sectionSheet ? sectionSheet.sheetName : null,
      },
      specColumns,
      hasAvailabilityColumn: productSheet.headers.includes("availability"),
      sections: sectionSheet ? readSections(sectionSheet.rows) : [],
      products: readProducts(productSheet.rows, specColumns),
    };
  };

  const buildImportPlan = async (workbookFile, imageFiles = [], ctx = {}) => {
    const buffer = await workbookFile.arrayBuffer();
    return validateImport(parseWorkbook(buffer), imageFiles, ctx);
  };

  return {
    findHeaderRow,
    pickSheet,
    parseWorkbook,
    validateImport,
    buildImportPlan,
  };
};

export default createCatalogueImport;
